import {useRef, useCallback, useEffect, forwardRef, useImperativeHandle} from 'react';
import {useField} from 'formik';
import ReactQuill from 'react-quill';
import TextEditor from './TextEditor';
import {convertTextBeforeSave} from './TextEditorHelpers';

export interface TextEditorFormikHandle {
    clear: () => void;
}

interface TextEditorFormikProps {
    name: string;
    limit?: {maxChar?: number; showCharCounter?: boolean; showMessagesAmount?: boolean; softLimit?: boolean};
    dependencies?: unknown[];
    showToolbar?: boolean;
    expanded?: boolean;
    variablesType?: string;
    lang?: string;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    minHeight?: string | number;
    placeholder?: string;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    disableVariables?: boolean;
    disable?: {video?: boolean};
}

const TextEditorFormik = forwardRef<TextEditorFormikHandle, TextEditorFormikProps>(function TextEditorFormik({
    name,
    limit = {},
    dependencies = [],
    showToolbar = false,
    expanded = false,
    variablesType = '',
    lang,
    prefix = null,
    suffix = null,
    minHeight = null,
    placeholder = '',
    onKeyDown,
    disableVariables = false,
    disable
}, ref) {
    const [field, , helper] = useField(name);
    const quillRef = useRef<ReactQuill | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
        clear: () => {
            quillRef.current?.getEditor().setText('');
            helper.setValue('');
        },
    }), [helper]);

    // Stop mouseup only on toolbar elements: prevents the parent Popup's outside-click
    // detector from firing on toolbar clicks, while still letting mouseup reach document
    // for clicks inside the editor — Quill needs that to track text selection (without it,
    // toolbar formats like alignment lose the active selection and only apply to the caret).
    useEffect(() => {
        const el = wrapperRef.current;
        if(!el) return;
        const stop = (e: Event) => {
            const target = e.target as Element | null;
            if(target?.closest?.('.ql-toolbar, .ql-picker-options')) {
                e.stopPropagation();
            }
        };
        el.addEventListener('mouseup', stop);
        el.addEventListener('touchend', stop);
        return () => {
            el.removeEventListener('mouseup', stop);
            el.removeEventListener('touchend', stop);
        };
    }, []);

    const handleChange = useCallback(() => {
        const quill = quillRef.current?.getEditor();
        const isEmpty = !quill || (quill.getLength() <= 1 && !quill.getText().trim());
        helper.setValue(isEmpty ? '' : convertTextBeforeSave(quillRef));
    }, [helper]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if(e.key === 'Enter') {
            e.stopPropagation();
        }
        if(onKeyDown) {
            onKeyDown(e);
        }
    }, [onKeyDown]);

    return (
        <div ref={wrapperRef} onKeyDown={handleKeyDown}>
            <TextEditor
                quillRef={quillRef}
                defaultText={field.value ?? ''}
                onChange={handleChange}
                onSave={handleChange}
                dependencies={dependencies}
                showToolbar={showToolbar}
                expanded={expanded}
                limit={limit}
                variablesType={variablesType}
                lang={lang}
                prefix={prefix}
                suffix={suffix}
                minHeight={minHeight}
                placeholder={placeholder}
                disableVariables={disableVariables}
                disable={disable}
            />
        </div>
    );
});

export default TextEditorFormik;
